import { api } from 'shared/api';
import { User, UserAuth } from 'entities/user/model/types';
import { ApiResponse } from 'shared/model/types';
import { setUserMeta } from 'shared/config/store/reducers/userSlice';
import { PURGE } from 'redux-persist/es/constants';

/**
 * Этот модуль определяет API-эндпоинты, связанные с данными пользователя.
 * Специфичные API-эндпоинты, которые относятся к конкретным страницам или функциям, не должны определяться здесь,
 * так как этот модуль предназначен только для ОБЩИХ нужд API пользователя.
 */
const userApi = api.injectEndpoints({
  endpoints: (build) => ({
    getUser: build.query<ApiResponse<UserAuth>, void>({
      query: () => ({
        url: '/user',
        method: 'GET',
      }),
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        const { data } = await queryFulfilled;
        if (data.response) dispatch(setUserMeta(data.response));
      },
    }),

    getUserInfo: build.query<ApiResponse<Partial<User>>, void>({
      query: () => ({
        url: '/user/info',
        method: 'GET',
      }),
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        const { data } = await queryFulfilled;
        if (data.response) dispatch(setUserMeta(data.response));
      },
    }),

    logout: build.mutation<ApiResponse<never>, void>({
      query: () => ({
        url: '/user/logout',
        method: 'POST',
      }),
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (error) {
          console.log('Logout API request failed:', error);
        }

        try {
          localStorage.removeItem('userData');
        } catch (error) {
          console.log('Failed to remove user data from AsyncStorage:', error);
        }

        dispatch({ type: PURGE, key: 'root', result: () => null });
        dispatch(api.util.resetApiState());
      },
    }),

    deleteUser: build.mutation<ApiResponse<never>, void>({
      query: () => ({
        url: '/user/private/user-delete',
        method: 'DELETE',
      }),
    }),

    getAvatar: build.query<string | null, void>({
      async queryFn(_arg, _queryApi, _extraOptions, fetchWithBQ) {
        const res = await fetchWithBQ({
          url: 'user/avatar',
          method: 'GET',
          responseHandler: (response: Response) => response.blob(),
          cache: 'no-cache',
        });

        if (res.error || !res.data) {
          return { data: null };
        }

        const url = URL.createObjectURL(res.data as Blob);
        return { data: url };
      },
      providesTags: ['user-avatar'],
    }),

    uploadAvatar: build.mutation<string, File>({
      async queryFn(file, _queryApi, _extraOptions, fetchWithBQ) {
        const formData = new FormData();
        formData.append('file', file);

        const res = await fetchWithBQ({
          url: 'user/avatar',
          method: 'POST',
          body: formData,
        });

        if (res.error) return { error: res.error };

        const data = res.data as { avatarUrl?: string };

        return { data: data?.avatarUrl ?? URL.createObjectURL(file) };
      },
      invalidatesTags: ['user-avatar'],
    }),

    deleteAvatar: build.mutation<void, void>({
      query: () => ({
        url: 'user/avatar',
        method: 'DELETE',
      }),
      invalidatesTags: ['user-avatar'],
    }),
    saveGtag: build.mutation<void, { clientId: string, eventName: string, gtag: string }>({
      query: (params) => ({
        url: 'user/gtag-event',
        method: 'POST',
        params
      }),
    }),
  }),
});

export const {
  useGetUserQuery,
  useLazyGetUserQuery,
  useGetUserInfoQuery,
  useLazyGetUserInfoQuery,
  useLogoutMutation,
  useDeleteUserMutation,
  useGetAvatarQuery,
  useLazyGetAvatarQuery,
  useUploadAvatarMutation,
  useDeleteAvatarMutation,
  useSaveGtagMutation,
} = userApi;

export default userApi;
